import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { FaHome, FaUsers, FaChartLine } from "react-icons/fa";  // Removed unused FaTicketAlt
import axios from "axios";
import { localserver, ROUTE_BASE_URL, commonBaseUrl } from "../../api/common";
import { MDBDataTable } from "mdbreact";
// Removed unused react-bootstrap imports (Row, Col, Card, ListGroup) - add back if needed elsewhere
import Chart from "react-apexcharts";  // This resolves correctly with ^1.9.0; avoids IIFE bundling issue

const EventsDashboard = () => {
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState("");
  const [getRecentAttendeesStatus, setGetRecentAttendees] = useState(false);
  const [recentAttendees, setRecentAttendees] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState(null);

  const formatMonth = (monthString) => {
    const date = new Date(monthString + "-01");
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    });
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "string") {
      return parseInt(value.replace(/,/g, "")) || 0;
    }
    return parseFloat(value) || 0;
  };

  const generateEventChartData = (apiData) => {
    if (!apiData) return [];

    const eventDashboard = apiData.event_dashboard?.data?.[0] || {};
    const eventPaymentData = eventDashboard.eventpaymentmonthwise || [];

    const eventPaymentMap = {};
    eventPaymentData.forEach((item) => {
      eventPaymentMap[item.month] = parseNumber(item.total_revenue);
    });

    const allMonths = new Set();
    eventPaymentData.forEach((item) => allMonths.add(item.month));

    const sortedMonths = Array.from(allMonths).sort();

    return sortedMonths.map((month) => {
      const eventRevenue = eventPaymentMap[month];

      return {
        month: formatMonth(month),
        eventRevenue: eventRevenue || 0,
      };
    });
  };

  const chartData = dashboardData ? generateEventChartData(dashboardData) : [];

  const fetchDashboardData = async () => {
    try {
      setEventLoading(true);
      const response = await axios.get(
        `${commonBaseUrl}/api/superadminDashboard`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.data) {
        setDashboardData(response.data);
        setEventError(null);
      } else {
        throw new Error("Failed to fetch dashboard data");
      }
    } catch (err) {
      console.error("Error fetching event dashboard data:", err);
      setEventError("Failed to load event analytics data");
    } finally {
      setEventLoading(false);
    }
  };

  const getRecentAttendees = async () => {
    setGetRecentAttendees(true);
    try {
      const response = await axios.post(
        `${localserver}/getAttendeesByAllEvent`,
        {
          event_id: 0,
          search: "",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        setRecentAttendees(response.data.attendees);
      } else {
        console.error("Error: Failed to fetch user data");
      }
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setGetRecentAttendees(false);
    }
  };

  useEffect(() => {
    getRecentAttendees();
    fetchDashboardData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRowClick = (item) => {
    setIsNavigating(true);
    localStorage.setItem("eventId", item.event_id);
    setTimeout(() => {
      setIsNavigating(false);
      history.push(`${ROUTE_BASE_URL}/event-attentees`);
    }, 1500);
  };

  const filteredAttendees = useMemo(() => {
    if (!searchQuery.trim()) {
      return recentAttendees;
    }
    return recentAttendees.filter(
      (item) =>
        item.first_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        item.last_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        item.email.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
        item.event_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        item.ticket_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  }, [recentAttendees, searchQuery]);

  const createAreaChartOptions = (
    categories,
    yAxisTitles,
    colors,
    tooltipFormatter
  ) => ({
    chart: {
      type: "area",
      height: 300,
      toolbar: { show: false },
    },
    colors,
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: {
        opacityFrom: 0.6,
        opacityTo: 0.1,
      },
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      title: { text: yAxisTitles[0] },
      labels: {
        formatter: (val) => {
          if (yAxisTitles[0].includes("Revenue")) {
            return `₹${(val / 1000).toFixed(0)}K`;
          }
          return `${(val / 1000).toFixed(0)}K`;
        },
        style: {
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      y: {
        formatter: tooltipFormatter,
      },
      x: {
        formatter: (val) => `Month: ${val}`,
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
    },
  });

  const eventAnalyticsOptions = createAreaChartOptions(
    chartData.map((item) => item.month),
    ["Event Revenue (₹)"],
    ["#0d6efd"],
    (val) => `₹${val.toLocaleString()}`
  );

  const eventAnalyticsSeries = [
    {
      name: "Event Revenue",
      data: chartData.map((item) => item.eventRevenue),
    },
  ];

  const renderLoadingSpinner = () => {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          color: "#666666",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #184778",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px auto",
          }}
        ></div>
        <p style={{ margin: "0", fontSize: "16px" }}>Loading tickets...</p>
      </div>
    );
  };

  const tableData = useMemo(
    () => ({
      columns: [
        {
          label: <div className="fw-bold">Name</div>,
          field: "name",
          sort: "asc",
        },
        {
          label: <div className="fw-bold">Registration Date</div>,
          field: "registerDate",
          sort: "asc",
        },
        {
          label: <div className="fw-bold">Event</div>,
          field: "event",
          sort: "asc",
        },
        {
          label: <div className="fw-bold">Tickets</div>,
          field: "ticket",
          sort: "asc",
        },
        {
          label: <div className="fw-bold">Email</div>,
          field: "email",
          sort: "asc",
        },
      ],
      rows: filteredAttendees.map((item) => ({
        name: (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <div className="fw-medium">
                {item.first_name} {item.last_name}
              </div>
            </div>
          </div>
        ),
        registerDate: (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <div className="fw-medium">
                {item.created_at
                  ? new Date(item.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "--"}
              </div>
            </div>
          </div>
        ),
        event: (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <div className="fw-medium">{item.event_name}</div>
            </div>
          </div>
        ),
        ticket: (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <div className="fw-medium">{item.ticket_name}</div>
            </div>
          </div>
        ),
        email: (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <div className="fw-medium">{item.email}</div>
            </div>
          </div>
        ),
        clickEvent: () => handleRowClick(item),
      })),
    }),
    [filteredAttendees]
  );

  if (getRecentAttendeesStatus) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #184778",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <span style={{ color: "#6b7280", fontSize: "16px" }}>
          Loading event overview...
        </span>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {isNavigating && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              textAlign: "center",
              color: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid white",
                borderTop: "4px solid #184778",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 16px",
              }}
            ></div>
            <p style={{ margin: "0", fontSize: "16px" }}>
              Navigating to event...
            </p>
          </div>
        </div>
      )}
      <section className="event_overview_main">
        <div className="container-fluid">
          <div className="brood_comes_div">
            <div className="row align-items-center">
              <div className="col-md-6 brood_comes_heading">
                <h2>
                  <div className="brood_comes_part">
                    <FaHome />
                  </div>
                  Dashboard
                </h2>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            minHeight: "60vh",
            backgroundColor: "#f9fafb",
            padding: "24px 15px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              padding: "32px",
              border: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                marginBottom: "10px",
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(to right, #f9fafb, #ffffff)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div className="p-2 rounded-circle bg-opacity-10">
                  <FaChartLine className="text-primary" size={20} />
                </div>
                <div>
                  <h5 className="mb-0">Event Analytics</h5>
                  <small className="text-muted">Event revenue</small>
                </div>
              </div>
            </div>
            {eventLoading ? (
              <div className="text-center py-4">
                <p className="text-muted">Loading event analytics...</p>
              </div>
            ) : eventError ? (
              <div className="text-center py-4">
                <p className="text-danger">{eventError}</p>
              </div>
            ) : chartData.length > 0 ? (
              <div className="pt-2">
                <Chart
                  options={eventAnalyticsOptions}
                  series={eventAnalyticsSeries}
                  type="area"
                  height={300}
                />
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">No event data available</p>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "24px 15px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
              padding: "32px",
              border: "1px solid #f0f0f0",
            }}
          >
            {getRecentAttendeesStatus ? (
              renderLoadingSpinner()
            ) : (
              <div style={{ overflowX: "auto" }}>
                <div
                  style={{
                    marginBottom: "10px",
                    padding: "20px 24px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "linear-gradient(to right, #f9fafb, #ffffff)",
                  }}
                >
                  <div
                    className="second_heading"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaUsers size={20} style={{ color: "#184778" }} />
                    <h5>Recent Attendees ({filteredAttendees.length})</h5>
                  </div>
                  <div style={{ position: "relative" }}>
                    <i
                      className="fa fa-search"
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666666",
                        fontSize: "14px",
                      }}
                    ></i>
                    <input
                      type="text"
                      placeholder="Search Attendees..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      style={{
                        padding: "10px 16px 10px 36px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        width: "250px",
                        transition: "border-color 0.2s ease-in-out",
                      }}
                    />
                  </div>
                </div>
                <MDBDataTable
                  noBottomColumns
                  className="table table-hover js-basic-example dataTable table-custom spacing5"
                  entriesOptions={[5, 10, 20, 25]}
                  entries={5}
                  paging
                  pagesAmount={5}
                  data={tableData}
                  sortRows={["name"]}
                  preserveScroll={true}
                  preserveState={true}
                  searching={false}
                  clickableRows={true}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default EventsDashboard;